import { cn } from '@heroui/react';
import {
  SELECTION_STROKE_SCREEN_PX,
  selectionChromeWorldMetrics,
} from './selectionChrome';

function clampZoom(zoom: number): number {
  return zoom > 0 ? zoom : 1;
}

export interface SelectionChromeFrameProps {
  /** 外框左上角（世界 / 页内坐标，与 stage `scale(zoom)` 一致） */
  left: number;
  top: number;
  /** 含外扩后的宽高（通常 element + pad） */
  width: number;
  height: number;
  zoom: number;
  rotation?: number;
  dashed?: boolean;
  opacity?: number;
  /** 默认 `var(--accent)`；表格单元格编辑等可传 `var(--primary)` */
  borderColor?: string;
  className?: string;
}

/**
 * 选中框描边：内层 `scale(1/zoom)` + 固定屏幕 px 描边，与 PageChrome 一致，
 * 任意工作区缩放下保持 100% 时约 1.5px 线宽。
 */
export function SelectionChromeFrame({
  left,
  top,
  width,
  height,
  zoom,
  rotation,
  dashed = false,
  opacity,
  borderColor = 'var(--accent)',
  className,
}: SelectionChromeFrameProps) {
  const z = clampZoom(zoom);
  const transformOrigin = `${width / 2}px ${height / 2}px`;

  return (
    <div
      className={cn('absolute pointer-events-none', className)}
      style={{
        left,
        top,
        width,
        height,
        opacity,
        transformOrigin,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
      }}
      aria-hidden
    >
      <div
        className="box-border"
        style={{
          width: width * z,
          height: height * z,
          transform: `scale(${1 / z})`,
          transformOrigin: 'top left',
          borderStyle: dashed ? 'dashed' : 'solid',
          borderWidth: SELECTION_STROKE_SCREEN_PX,
          borderColor,
          borderRadius: SELECTION_STROKE_SCREEN_PX,
        }}
      />
    </div>
  );
}

/** 由元素矩形 + zoom 计算选框外框几何（世界坐标） */
export function selectionChromeFrameAroundElement(
  layout: { x: number; y: number; rotation?: number },
  size: { width: number; height: number },
  dragOffset: { dx: number; dy: number },
  zoom: number,
) {
  const { inset, pad } = selectionChromeWorldMetrics(zoom);
  return {
    left: layout.x + dragOffset.dx - inset,
    top: layout.y + dragOffset.dy - inset,
    width: size.width + pad,
    height: size.height + pad,
    rotation: layout.rotation,
  };
}
